import { MouseEvent } from 'react';

import { MoreVert } from '@mui/icons-material';
import {
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';

import { selectViewAllModal, setViewAllModal } from 'state/detourSlices';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { ODAnalysis } from 'state/originDestinationSlices';
import {
  selectCurrentAnalysisId,
  selectCurrentProjectInfo,
} from 'state/workflowSlice';

import { WorkflowType } from 'appConstants';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { useMenu } from 'features/workflow_routemonitoring/hooks/useMenu';

import { useODUIMode } from '../hooks/useODUIMode';

import styles from '../../common/common.module.css';

interface IODCardMenu {
  analysis: ODAnalysis;
}

export default function ODCardMenu({ analysis }: IODCardMenu) {
  const dispatch = useAppDispatch();

  const currentAnalysisId = useAppSelector(selectCurrentAnalysisId);
  const viewAllModal = useAppSelector(selectViewAllModal);
  const userProject = useAppSelector(selectCurrentProjectInfo);

  const { project_slug } = userProject;
  const { anchorEl, open, onClose, onOpen } = useMenu();
  const { setViewMode, setCopyMode } = useODUIMode();

  const handleClose = () => {
    if (viewAllModal) dispatch(setViewAllModal(false));
    onClose();
  };

  const onLink = async (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    try {
      const params = new URLSearchParams({
        project: project_slug,
        workflow: WorkflowType.OriginDestination,
        analysis: analysis.analysis_id,
      });

      const link = `${window.location.origin}/app?${params.toString()}`;
      await navigator.clipboard.writeText(link);
    } catch (err) {
      console.error('Clipboard copy failed: ', err);
    }
    onClose();
  };

  const onCopy = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setCopyMode(analysis);
    handleClose();
  };

  const onView = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setViewMode(analysis);
    handleClose();
  };

  const showViewOption =
    currentAnalysisId !== analysis.analysis_id || viewAllModal;
  return (
    <>
      <IconButton
        size="small"
        aria-label="More options"
        aria-controls={open ? 'analysis-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={onOpen}
      >
        <MoreVert fontSize="small" sx={{ color: 'white' }} />
      </IconButton>
      <Menu
        className={styles.menu}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
      >
        {showViewOption && (
          <MenuItem onClick={onView} className={styles.menu_item}>
            <ListItemIcon aria-label="view analysis">
              <FontAwesomeIcon name="square-arrow-up-right" size="0.75rem" />
            </ListItemIcon>
            <Typography variant="caption">View Analysis</Typography>
          </MenuItem>
        )}
        <MenuItem onClick={onLink} className={styles.menu_item}>
          <ListItemIcon aria-label="copy link">
            <FontAwesomeIcon name="link" size="0.75rem" />
          </ListItemIcon>
          <Typography variant="caption">Copy link</Typography>
        </MenuItem>
        <MenuItem onClick={onCopy} className={styles.menu_item}>
          <ListItemIcon aria-label="copy">
            <FontAwesomeIcon name="copy" size="0.75rem" />
          </ListItemIcon>
          <Typography variant="caption">Copy</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
